import { or, and, not, createMachine } from 'xstate';

import { routePath } from 'config/route-config';
import { actionUrl } from 'config/static-content';
import { events } from 'machines/config/events';
import { guards } from 'machines/config/guards';
import { stringToBool } from 'utils/misc';

export const directSalesMachineConfig = {
  id: 'directSalesMachine',
  context: {
    guards: {}
  },
  initial: 'home',
  states: {
    home: {
      on: {
        NEXT: [
          {
            target: 'fuelType',
            guard: 'hasAnyMeterValidationRequired'
          },
          {
            target: 'selectOptions',
            guard: 'hasMeterExceptions'
          },
          {
            target: 'mixedMeters',
            guard: 'hasMixedMeters'
          },
          {
            target: 'personaliseQuote'
          }
        ],

        BLOCK_JOURNEY: {
          target: 'businessUpdatesAnnouncement'
        }
      },
      meta: {
        path: routePath.home
      }
    },

    businessUpdatesAnnouncement: {
      on: {
        NEXT: {
          target: 'businessUpdatesSubmitEmail'
        },
        ALLOW_JOURNEY: {
          target: 'home'
        }
      },

      meta: {
        path: routePath.businessUpdatesAnnouncement
      }
    },

    businessUpdatesSubmitEmail: {
      on: {
        NEXT: {
          target: 'businessUpdatesSubmitEmailSuccess'
        }
      },
      meta: {
        path: routePath.businessUpdatesSubmitEmail
      }
    },

    businessUpdatesSubmitEmailSuccess: {
      meta: {
        path: routePath.businessUpdatesSubmitEmailSuccess
      }
    },

    fuelType: {
      on: {
        NEXT: [
          {
            target: 'meterpointValidation',
            guard: 'meterValidationRequired'
          },
          {
            target: 'selectOptions',
            guard: 'hasMeterExceptions'
          },
          {
            target: 'personaliseQuote'
          }
        ],
        CHANGE_ADDRESS: {
          target: 'home'
        }
      },
      meta: {
        path: routePath.fuelType
      }
    },

    meterpointValidation: {
      on: {
        NEXT: [
          {
            target: 'selectOptions',
            guard: 'hasMeterExceptions'
          },
          {
            target: 'mixedMeters',
            guard: 'hasMixedMeters'
          },
          {
            target: 'personaliseQuote'
          }
        ],
        GUIDE_CLICK: {
          target: 'meterPointGuide'
        }
      },
      meta: {
        path: routePath.meterpointValidation
      }
    },

    meterPointGuide: {
      meta: {
        path: routePath.meterpointGuide
      }
    },

    selectOptions: {
      on: {
        ELIGIBLE_FUEL: {
          target: 'personaliseQuote'
        },
        EDIT_ADDRESS: {
          target: 'home'
        },
        HOME_MOVE: [
          {
            target: 'externalContactUs',
            description: `or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])`,

            guard: or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])
          },
          {
            target: 'externalMoveIn',
            guard: 'isBGECustomer'
          },
          {
            target: 'externalBgClassicHomeMove'
          }
        ]
      },
      meta: {
        path: routePath.selectOptions
      }
    },

    mixedMeters: {
      on: {
        NEXT: {
          target: 'personaliseQuote'
        }
      },
      meta: {
        path: routePath.mixedMeters
      }
    },

    personaliseQuote: {
      on: {
        NEXT: [
          {
            target: 'evTariffInformation',
            guard: 'showEvTariffInformation'
          },
          {
            target: 'paymentMethod',
            guard: 'isPrePayOnly'
          },
          {
            target: 'consumptionEntry'
          }
        ]
      },
      meta: {
        path: routePath.personaliseQuote
      }
    },

    evTariffInformation: {
      on: {
        NEXT: [
          {
            target: 'consumptionEntry'
          }
        ]
      },
      meta: {
        path: routePath.evTariffInformation
      }
    },

    paymentMethod: {
      on: {
        NEXT: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.prepayInfo
      }
    },

    consumptionEntry: {
      on: {
        NEXT: [
          {
            target: 'emailCapture',
            description: "and(['releaseAbandonedCart', 'isEligibleCustomer'])",
            guard: and(['releaseAbandonedCart', 'isEligibleCustomer'])
          },
          {
            target: 'quote',
            guard: 'isEligibleCustomer'
          },
          {
            target: 'personalInformationSummary'
          }
        ]
      },
      meta: {
        path: routePath.consumptionEntry
      }
    },

    emailCapture: {
      on: {
        NEXT: [
          {
            target: 'quote',
            guard: 'isEligibleCustomer'
          },
          {
            target: 'personalInformationSummary'
          }
        ]
      },
      meta: {
        path: routePath.emailCapture
      }
    },

    personalInformationSummary: {
      on: {
        NEXT: {
          target: 'externalBgSalesApp'
        },
        CHANGE_ADDRESS: {
          target: 'home'
        },
        CHANGE_CONSUMPTION: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.personalInformationSummary
      }
    },

    quote: {
      on: {
        NEXT: {
          target: 'createAccount'
        },
        EMAIL_QUOTE: {
          target: 'emailQuote'
        },
        CONSUMPTION: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.quote
      }
    },

    emailQuote: {
      on: {
        NEXT: {
          target: 'quote'
        }
      },
      meta: {
        path: routePath.emailQuote
      }
    },

    createAccount: {
      on: {
        NEXT: [
          {
            target: 'directDebitType',
            description:
              "and(['hasMultipleDirectDebitTypes', 'isAnyDirectDebit'])",
            guard: and(['hasMultipleDirectDebitTypes', 'isAnyDirectDebit'])
          },
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'psrEntry'
          }
        ],
        CHANGE_ADDRESS: { target: 'home' }
      },
      meta: {
        path: routePath.createAccount
      }
    },

    directDebitType: {
      on: {
        NEXT: {
          target: 'directDebit'
        }
      },
      meta: {
        path: routePath.directDebitType
      }
    },

    directDebit: {
      on: {
        NEXT: {
          target: 'psrEntry'
        }
      },

      meta: {
        path: routePath.directDebit
      }
    },

    psrEntry: {
      on: {
        NEXT: [
          {
            target: 'psrCapture',
            guard: 'hasPSR'
          },
          {
            target: 'userPreferences'
          }
        ]
      },
      meta: {
        path: routePath.psrEntry
      }
    },

    psrCapture: {
      on: {
        NEXT: {
          target: 'userPreferences'
        }
      },
      meta: {
        path: routePath.psrCapture
      }
    },

    userPreferences: {
      on: {
        NEXT: {
          target: 'orderSummary'
        }
      },
      meta: {
        path: routePath.userPreferences
      }
    },

    orderSummary: {
      on: {
        NEXT: {
          target: 'orderComplete'
        }
      },
      meta: {
        path: routePath.orderSummary
      }
    },

    orderComplete: {
      meta: {
        path: routePath.orderComplete
      }
    },

    externalBgSalesApp: {
      meta: {
        path: actionUrl.bgSalesApp
      }
    },

    externalContactUs: {
      meta: { path: actionUrl.contact }
    },
    externalBgClassicHomeMove: {
      meta: { path: actionUrl.bgClassicHomeMoveUrl }
    },

    externalMoveIn: {
      meta: { path: actionUrl.moveIn }
    }
  },
  ...events
};

const { VITE_XSTATE_DEBUG } = process.env;
const debug = stringToBool(VITE_XSTATE_DEBUG);
// for VS extesnion
/* istanbul ignore next -- @preserve */
debug &&
  createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QQJYCcwGMAuBlAhgDZwCy+mAFigHZgB0FA9gLZgDEAcgKIAaAKgG0ADAF1EoAA6NYKbCkbVxIAB6IATGoCMdAGw7NAFiEGAHAFYzBgOw2ANCACeiTSasG6Qs1YDMVnWrMATjUhTTUAX3D7VAwcAmJYMkoaeiZWTl5BTTEkECkZOQUlVQQNbT1DY3NLGyt7JwRvTzU6MyEdM28zNVNTc0jo9Cw8IlJyKloGFnZufgE1HMlpWXlFXJKy3X0jUwtrO0d1IVC6Ny6TTVCTQIvvAZAY4fix5Mm0mcyBb0W85cK10AbLRbSq7GoHBoGHRCOgXTRhEwGHrdVw6e6POKjRLjFJTdIAIQAMgB5ADCAGkAPoAKWJAFUAErcACawh++RWRXW6mBFR21X2dUOCE0TSsdECUNMxz8nQM6KGmISSQm9AARgBXGS0WCwOkSCD4bBwACC1GojA11EwYFY1GwGTmoiUHP+xUQgRcdE0ns6Ll81hMJnqziaLW8OlMNjU5k0ViEESiD0VI2VOMmmu1cD1BqNpvNluttrA9rYJsJJIA6jT6UyuKznblXat3Y0rNp454hE0UV0DCGRQETN6QoEvAYmuZPArYqmXqq6JmUrr9YbjbBcBq1cxZFxmPgUIRHYJG0sCi3uSKDF46DHQsdLIidL4B6KE3QI1Hzs+1JGZ08sRVXEADMNTAQg+AcCQPiddk-gvQFnGvcU70uTxemfIUGjjAxAg-dtfzjMwwiEEx-yVecQLAiCoJgrI4PPLlEKvG9UIfDCX2FFwLg-SdvAMXDNGI69yLnbFXnoUDwMg6Dj3mBjOQBFQkNYkx73Qp9OOwpFyiEQJ9ECUiZREpMMTEoDJikmjZNJAAJE0OAAcS4SkTQAETchkuFwXA2RdeCmOUhB9O0NRAnbfQ+L8QJXysTpdAEyNMI6bxNFE55xIXVhjTQKQaGwAA1IgUDXVY5NPX5GKUjZ4WHboIx9Aj4X7Li-BhT0LhjDpRRjKx0sA9N6GysBcsYfKisIEqjTK2Z6P8qrWy0L16oMprDFi+EPGCPQg3MG5EX6tMJLoYbRvG4rSoUOSFnmxTFtq1o1Aa8Kwma18KlvHR4zfC5AiaeVTJTDKLKGsAcry+0Jqm-42EcukAEk3Jc0lCXhik-KbALqvUB6Vsa171q4ywWgTJ6fAfDRAkOyjJlgcDhmJCR-lgNguFRxz4aJFyADE6TZjGzzuy81CsAJTjMPRMICa8voHJ6kVvEJSPCtxOh6anMtxOniBwRnmdZtz4b4VyPK8nyBcqoXmKesWkWsFwoSCfQdDlv6Sb+tTOg6C4TDuQHZ2Bwa6G1hmmdWFnbOJEgXJIYkCq4C3m0CjZRbMcXJd8aWJaw9RvGCDxr0sMc89cdX-YAo6FxD3Ww4UCOo5juOE+yW63WF1P070TPLGzuXiPDb3ryhPx4Q1kHg-pmv9cj6PKVj+P5NbhCgpFsW4oz1OZZz0ovBhapiOixFcLRcuKM1yYd2USASDBkaWdmxOscWjv167zfe64wJwo-SUai0L7LiJkGAHAax1oJoFgAoYqdMACKGpGDGnKgpNuzF+LdgSqLVwRcDCGBdlxfQLQjDHG7GOdsNwT7AIrjTeg4DIHUGgWAOBCC6ICBbpjBal40HeAwb1bBuD3rxlhNcLwFwdAmC+r7MeQdaFQMmrA+BiCH43XYVbIKXCeFYNwjgqEr5BISlcHGaEFhNA6ElFI46YAABufB8BoBQMBYC8NqDAUYGgfcMMH4VSTtjRo1wdCtHQqlUhEszDvTCHQIhxw85eB9AdU+5lpH4AcHabAN9sBMAgEgpeydEDeD8QEywQSvAhIEXva4eStBhX0kIPq8TA7HUwHXDUzBa7UC4PaNADgskqJQWo-JbRCk+mKR0AcJgNAFzHIXYSYUgHJhAZXXEjTqCwGaa09p2BOlyTYYLXpJQ87Xn0deHov4rAXFCcKX8npPoXFwhLQx3ZzELhkfQwgTiXFuOmgoTczB9ybM8cg5eJQOrel9KlX2bhTnBgubvWEFhvD-yCD4ISjzcTPKIG81x7jVjfN+V0uyDlnIm08t5XyXin6cP0BE8h3YrDBBwZKbwctaUoRCNeBM9U4y1MoWfceaLXnOMxZ86gOLbFdN5mzSkfBmQAAUE5ko4cxYFPohJgoDJCuWYQWhCWfEifiLgnoosmHyjFHz-gis2SQeGPAuBuTnlwPgXAGSkoBTk4KXplV+nBYGKFDQtDtn0SY9o7Qj5mENTQu+siTVYq+c03FbBZVOuJBwcs8NcAuRgXSYkDrH4KqCkq0F-oIVBjlpsNSot9ClzaL4MNdBjUCtNdi2Noq2D4qcsjJNuA6QkGlXweGSac2qKBe6gtXr1UXLhLoOEPg2gBBqTWgAjgolh8rB0emHSqwt3qBxmB4hoIw5hTnQl2gupdrMSAmnhoSSkGas1ypdT4-NG7R3Fq4sG281xQh+ChO0KmdTQELkXcwltHau09r7RwAduy13Dg9aqotPrEDfV0BGdsX7JQPhPUB8VV6pWysg4C6DIKn1qpfdhAILROjBC0KYeEtVMOIMtda210cHVOvw66x9nqSMIZFC4bQY5WUS0RGFUW9H2AJtwEmlNabr2ZuzSuqDbqYMju4wOSUhD-C+H4nKHdANuUJOOtgWx9jgJuTBgeQg99PgKYIyxFCak0KPihFpRAO7QpPSDOFawvhSI1ttBZphijrP3tbJcZ8ASkTdjCKcuKmhXxXAic+Bqwn7yhr-QsyYmAMB5hNJgRpVoHT-OyT4padUnqrQJi1BoPF4VhVCAJfa3Qa1ZbADlvLhZCufG2ZbRTpXHrPTWlVnkKFvARkRG4SMT0-rNey8aXL+XSxKJC8LXG5X8ZLSG40IwP8xmjYZaleFem5lUPPvQFrbWFsOlbYS9yxLzY2dddq9q4VnxxljHnNQA5au6HU92cbYQXs1rMmZtUsgZLLuW9bF+Es35Z1lq+-xYzSJCYEvs9sQOUwg9kN0nZtn1GRkwXFLR-CLk+n0ZYINOCYwCTIul6htbYBoHWX84LxXWx5P0gUicQz17nN9WToSIRRbxlpW0XzdPTsM6Zx0rpD9uvePZ-0wJPOSnCj260SMhkg1f1pX7fT9SnmM9JPgJmGoMA4567Zicgi86xLezOkZpO8KC5qSLGpY5ji0-1-+3EWoRrSowMBEaJYbRWdgmzy8UpxTcTil0X8Is4tq-hR+ExQZ4XXFpWImtriIAjXNbL1nPSreehaL+BMtKjCekTw0Ub4YTFiNTpcWliYkwWlz-AXIZkDcpAj8xAAtHghofe05f1H2PsfE4a3vF70FJEX3uzuA6NCGWwa3A1qXDqHMa58wWitDaFJM+Sgw6pa4LopjHaeFfGGVo8LaWuD+m4Vw6+tTLi33mDcW4dzYD3BZw-zhDG3gvSe4i57ZX7vhqx+K3BN5e7HY8pBwb7Zirjv6bjbi7j7iHibh5bZh-4iiygSjhQxL8RuD6SfZcTX4QH6RQE1IwFd4+6WTUTg44FhB5wShGI3CiwiaiivgGKwh+CjZ2xdDRI1qnQQyFQXRCpMHiIj5DwhBPQc4xStS4Q37BA2A7rdhXDCG3xoDSpjT2iOQaglRgA4HkbDiIigqhDRY9CvjEwgrdgE4dR5JHa0EZb0DVzYB6zhzGGdDR6VBiI1KC7V7qAWB4TWAdB6SZ407CEoBXwQBpJ3xeGjYRJhS7SUY+ikHYQ64a4BB5xRJBJcqwEGZPIRovIoDyLMI4GpRtAeA7p+A+CIiXAuYigfQTjEQJhBinJfx+bWLGYOJRoSFF6uo+bcK4RxTqZSiCFhIUYSIqw9ANZ64FHd5GpJIpJpIZIVHtgwg1Hfr+D7SkQCLaCeA+A+CegdDxizLOH05LIrItL-DM4NC46uo4JjKtDBDEQWBkztiTEa4dHRgCS4Q1p1rvLRrCpNqdI4FqGTr6QRT+j6CBGlDMofhBgU6eBjLiLzEXGS6AbGg4FxTijUEhADIE5+Cvjla6Aix+IxiGT+A1pGZ2IOJmZGaHgd4PE+LMEhFqR6CmAaBVA8ZuY-zcQqolxzoS7jz+aHiBZGEDGskAGcHAE1KgEI4tBYIhCih7CuBOFAx0FnazZgDzYdaSF6CnBBIhDwoMq+xfZNASgThlpfRlC-ganzL07A5gCg7YCMFSmLTdB4TdBhTQhfR6SmDxb+I3CGSJSmIcG-gY6zhY7YAVGp43407ESShoSbbcneidB5ynLkl-Rpbe4uFS53HrEsGHY6T+hhCD65KJGpQV5nGBgxgAlG4m7YBm6SksmtiRYj5HxxiSjoaMpO4fhKyRhaISxRI1p+7aGB7B5FjMmW6PHebeiIj37dCQp86VmEKLlxQ1KRjtDoman5k5556gn3Gzk+LWBKE3AmL9ymIA6rmNDJ4YS0o0piJP4ilBwHloCkgsASDEDYkemXhtBCTVHVnthJliIWnrnDyGS+DPllx5n05gDKA5QvL4hQAZQmgSASBMEylAGkQgF5x9z+oJhdABlTg7oUILFal0AIVIVECfn2jkDYB0gzkK6cLlkeBjKNQJhQiGDbzHLijfriKihp57p+aIUjTIVQCkiED4C6goCYC2TTAkCMCWKtknns5sWkRaDhRcXbC8UxIeDyzNSnGiU0WEBKUqVOJxm-jsVaW1m6V9zhIZnHHyziIt7hBAA */ ...directSalesMachineConfig
    },
    {
      guards
    }
  );
